import { useEffect, useState } from 'react'

import { ContentAST } from '../../transforms/ast'

export function useActiveHeadingTracking(
  currentContent: ContentAST | null,
  currentChapterFilename?: string,
  suppressSidebarAutoScroll = false,
) {
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null)

  useEffect(() => {
    const headings = currentContent ? currentContent.blocks.filter((block) => block.type === 'heading') : []

    if (headings.length === 0) {
      setActiveHeadingId(null)
      return
    }

    const headingIds = new Set(headings.map((heading) => heading.id))

    const updateActiveHeading = () => {
      // Query fresh each time because Lexical may replace DOM nodes.
      const headingEls = Array.from(document.querySelectorAll('[data-block-id]')).filter((el) =>
        headingIds.has(el.getAttribute('data-block-id') || ''),
      ) as HTMLElement[]

      if (headingEls.length === 0) {
        return
      }

      // Pick the last heading at or above 55% of viewport.
      // scrollToBlock uses scrollIntoView({ block: 'center' }) which centers within
      // the editor container, so this threshold keeps the sidebar and viewport aligned.
      const threshold = window.innerHeight * 0.6

      let bestId: string | null = null
      let bestTop = -Infinity
      for (const el of headingEls) {
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold && rect.top > bestTop) {
          bestTop = rect.top
          bestId = el.getAttribute('data-block-id')
        }
      }
      setActiveHeadingId(bestId)
    }

    // Capture-phase listener catches scroll on any element because scroll doesn't bubble.
    document.addEventListener('scroll', updateActiveHeading, { capture: true, passive: true })
    const initTimer = setTimeout(updateActiveHeading, 200)

    return () => {
      document.removeEventListener('scroll', updateActiveHeading, { capture: true })
      clearTimeout(initTimer)
    }
  }, [currentContent, currentChapterFilename])

  useEffect(() => {
    if (suppressSidebarAutoScroll) {
      return
    }

    if (!activeHeadingId) {
      return
    }

    const el = document.querySelector(`[data-heading-id="${activeHeadingId}"]`) as HTMLElement | null
    if (!el) {
      return
    }

    let scrollParent: HTMLElement | null = el.parentElement
    while (scrollParent) {
      const style = getComputedStyle(scrollParent)
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        break
      }
      scrollParent = scrollParent.parentElement
    }

    if (!scrollParent) {
      el.scrollIntoView({ block: 'center' })
      return
    }

    const parentRect = scrollParent.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const targetTop = parentRect.top + parentRect.height / 2 - elRect.height / 2
    const offset = elRect.top - targetTop
    scrollParent.scrollBy({ top: offset, behavior: 'smooth' })
  }, [activeHeadingId, suppressSidebarAutoScroll])

  return activeHeadingId
}
